import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMinus, faPlus } from '@fortawesome/pro-regular-svg-icons';
import { cn } from '@v2/shared/lib/utils';

type Props = {
  description: string;
};

/**
 * The category's copy, clamped to three lines with a Show More beneath it.
 *
 * Every word of it is in the DOM whether or not it is expanded — the clamp is CSS, not a
 * truncation — because this copy is most of what the page says about itself and these
 * pages are crawled for exactly that. The legacy page did the same thing with a class
 * toggle; what has changed is only that the collapsed height is now a line count rather
 * than a fixed pixel height, so it does not cut a line in half at an awkward width.
 */
export default function CategoryDescription({ description }: Props) {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!description) {
    return null;
  }

  return (
    <div>
      <p className={cn('m-0 text-sm leading-relaxed text-slate-600', !isExpanded && 'line-clamp-3')}>
        {description}
      </p>

      <button
        type="button"
        aria-expanded={isExpanded}
        onClick={() => setIsExpanded((expanded) => !expanded)}
        className="mt-1 cursor-pointer border-0 bg-transparent p-0 text-sm font-semibold text-primary"
      >
        <FontAwesomeIcon icon={isExpanded ? faMinus : faPlus} className="mr-1 text-xs" aria-hidden />
        {isExpanded ? 'Show Less' : 'Show More'}
      </button>
    </div>
  );
}
